import React, { useState, useEffect, useRef, useContext } from "react";

import { useTranslation } from 'react-i18next';
import { AuthContext } from "../../../authContext";
import './users.css'

import ConfirmDeleteModal2 from "../../../components/DeletAssitant";

import LoadingSpinner from "../../../components/LoadingSpinner";

const CustomDropdown = ({ placeholder, options, selected, setSelected, disabled, errorClassName = '', multiSelect = false }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleSelect = (value) => {
        if (multiSelect) {
            setSelected(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
            return;
        }
        setSelected(value);
        setOpen(false);
    };

    const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const hasSelection = multiSelect ? selected.length > 0 : !!selected;
    const displayLabel = multiSelect ? selected.join(', ') : selected;
    const selectInputClasses = `select-input ${open ? 'open' : ''} ${hasSelection ? 'selected' : ''} ${errorClassName ? 'input-error' : ''}`;

    return (
        <div className={`select-container ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
            <div
                className={selectInputClasses}
                onClick={() => !disabled && setOpen(prev => !prev)}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                tabIndex={0}
            >
                {hasSelection ? displayLabel : <span className="placeholder">{placeholder}</span>}
                <img src="/imagens/seta.svg" className="select-icon" alt="seta" />
            </div>
            {open && (
                <ul className="dropdown">
                    {options.map((option, idx) => (
                        <li
                            key={idx}
                            className={multiSelect && selected.includes(option) ? 'dropdown-option-selected' : ''}
                            onClick={() => handleSelect(option)}
                        >
                            {option}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

function UsersContent() {
    const { t: tManagement } = useTranslation('management');
    const { t: tCommon } = useTranslation('common');
    const { accessToken } = useContext(AuthContext);

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    // is_manager (2026-08-04): pergunta obrigatória, sem default — null
    // significa "ainda não escolhido", diferente de false ("não é manager").
    const [isManager, setIsManager] = useState(null);
    // Grupos desativados por ora (voltamos a usar um campo de role dedicado
    // em vez de grupos) — ver CADASTRO_USUARIO_EXTERNO.md. Descomentar junto
    // com /get_groups no backend se essa frente voltar.
    // const [grupos, setGrupos] = useState([]);
    // const [availableGroups, setAvailableGroups] = useState([]);
    const [camposValidos, setCamposValidos] = useState(false);
    const [formTocado, setFormTocado] = useState(false);

    const [alertType, setAlertType] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [isRegistering, setIsRegistering] = useState(false);

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    const truncate = (s, n = 32) => (s?.length > n ? s.slice(0, n - 1) + '…' : s);

    useEffect(() => {
        setCamposValidos(!!(nome && email && role) && isManager !== null);
    }, [nome, email, role, isManager]);

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleConfirmDelete = async () => {
        const userToDelete = selectedUser;
        setUsers((prev) => prev.filter((u) => u.email !== userToDelete.email));

        handleCloseModal();

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_user`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ email: userToDelete.email }),
            });

            const result = await response.json();

            if (!result.success) {
                console.error(result.message || result.error);
                setUsers((prev) => [...prev, userToDelete].sort((a, b) => a.name.localeCompare(b.name)));
            }
        } catch (error) {
            console.error('Erro ao deletar usuário:', error);
            setUsers((prev) => [...prev, userToDelete].sort((a, b) => a.name.localeCompare(b.name)));
        }
    };

    const handleExitModal = () => {
        setShowModal(false);
        resetFormularioCadastro();
    };

    const resetFormularioCadastro = () => {
        setNome('');
        setEmail('');
        setRole('');
        setIsManager(null);
        setFormTocado(false);
    };

    const handleCadastro = async () => {
        setFormTocado(true);
        if (!camposValidos) return;

        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValidEmail) {
            setAlertType("error");
            setAlertMessage(tManagement('email_valido') || "Digite um e-mail válido");
            setShowAlert(true);
            return;
        }

        setIsRegistering(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/post_invite_user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    name: nome,
                    email: email,
                    role: role,
                    is_manager: isManager
                })
            });

            const data = await res.json();
            if (data.status) {
                setShowModal(false);
                resetFormularioCadastro();
                setRefetchTrigger(prev => prev + 1);
                setAlertType("success");
                setAlertMessage(tManagement('cadastrado_com_sucesso') || "Usuário cadastrado com sucesso!");
                setShowAlert(true);
            } else {
                console.error("Erro:", data.error);
                setAlertType("error");
                setAlertMessage(data.error || "Erro ao cadastrar usuário");
                setShowAlert(true);
            }
        } catch (err) {
            console.error("Falha na API:", err);
            setAlertType("error");
            setAlertMessage("Falha na comunicação com o servidor");
            setShowAlert(true);
        } finally {
            setIsRegistering(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const usersResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_list_non_admin`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                });

                const usersData = await usersResponse.json();
                const normalized = Array.isArray(usersData)
                    ? usersData.map((u) => ({ email: u.email, name: u.user }))
                    : [];
                setUsers(normalized);
            } catch (err) {
                console.error('Erro ao buscar usuários:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [accessToken, refetchTrigger]);

    // Busca de grupos desativada por ora (ver comentário nos estados acima).
    // useEffect(() => {
    //     async function fetchGroups() {
    //         try {
    //             const res = await fetch(`${process.env.REACT_APP_API_URL}/get_groups`, {
    //                 method: 'GET',
    //                 headers: {
    //                     'Content-Type': 'application/json',
    //                     'Authorization': `Bearer ${accessToken}`
    //                 }
    //             });
    //             const data = await res.json();
    //             setAvailableGroups(data.status ? data.groups : []);
    //         } catch (err) {
    //             console.error('Erro ao buscar grupos:', err);
    //             setAvailableGroups([]);
    //         }
    //     }
    //
    //     fetchGroups();
    // }, [accessToken]);

    if (loading) return <LoadingSpinner />;

    const filteredUsers = users
        .filter(user =>
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="mod-user-grid" data-testid="mod-user-grid">
            <div className="mod-user-header-wrapper">
                <button
                    className="rl-btn-icon-mod-user"
                    title={tManagement('user_btn')}
                    onClick={() => setShowModal(true)}
                >
                    +
                </button>
                <div className="search-container-management-mod-user">
                    <input
                        type="text"
                        className="publish-input-mod-user"
                        placeholder={tManagement("buscar")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <img
                        src="/imagens/search.svg"
                        alt={tCommon("searchIcon")}
                        className="publish-search-icon-mod-user"
                    />
                </div>
            </div>

            {filteredUsers.length === 0 && (
                <p className="no-mod-users-msg">{tManagement("no_users")}</p>
            )}

            {filteredUsers.map((user) => (
                <div className="mod-user-card" key={user.email}>
                    <div className="mod-user-card-top">
                        <div className="mod-user-card-header">
                            <div className="mod-user-avatar">{user.name?.[0]?.toUpperCase()}</div>
                            <div className="mod-user-info">
                                <p className="mod-user-name">{user.name}</p>
                                <p className="mod-user-email" title={user.email}>
                                    {truncate(user.email, 32)}
                                </p>
                            </div>
                        </div>

                        <img
                            className="mod-user-delete"
                            src="/imagens/exclude-red.svg"
                            alt={tManagement('remove_user_tooltip')}
                            title={tManagement('remove_user_tooltip')}
                            onClick={() => handleDeleteClick(user)}
                        />
                    </div>
                </div>
            ))}

            <ConfirmDeleteModal2
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmDelete}
            />

            {showModal && (
                <div className="modal-overlay">
                    <div className="popUp">
                        <div className="popUp-header">
                            <h2 className="popUp-header-tittle">{tManagement("cadastrar")}</h2>
                            <img
                                src="/imagens/x.svg"
                                alt={tCommon('closeIcon')}
                                className="popUp-header-icon"
                                onClick={handleExitModal} />
                        </div>
                        <div className="popUp-input-group">
                            <span className="popup-span">{tManagement("user")}</span>
                            <input className={`input-name ${formTocado && !nome ? 'input-error' : ''}`} type="text" placeholder={tManagement("username")} value={nome} onChange={(e) => setNome(e.target.value)} />
                            <span className="popup-span">Email</span>
                            <input className={`input-email ${formTocado && !email ? 'input-error' : ''}`} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                            <div className="select-wrapper">
                                <span className="popup-span">{tManagement("role")}</span>
                                <CustomDropdown
                                    placeholder={tManagement("rolePlaceholder")}
                                    options={[tManagement("roleExterno"), tManagement("roleUser")]}
                                    selected={role === "externo" ? tManagement("roleExterno") : role === "user" ? tManagement("roleUser") : ""}
                                    setSelected={(label) => setRole(label === tManagement("roleExterno") ? "externo" : "user")}
                                />
                            </div>
                            <div className="select-wrapper" style={{marginTop:"16px"}}>
                                <span className="popup-span">{tManagement("isManagerQuestion")}</span>
                                <div className="is-manager-toggle" data-testid="is-manager-toggle">
                                    <button
                                        type="button"
                                        className={`is-manager-option ${isManager === true ? 'selected' : ''} ${formTocado && isManager === null ? 'input-error' : ''}`}
                                        onClick={() => setIsManager(true)}
                                        data-testid="is-manager-yes"
                                    >
                                        {tCommon("yes")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`is-manager-option ${isManager === false ? 'selected' : ''} ${formTocado && isManager === null ? 'input-error' : ''}`}
                                        onClick={() => setIsManager(false)}
                                        data-testid="is-manager-no"
                                    >
                                        {tCommon("no")}
                                    </button>
                                </div>
                            </div>
                            {/* Grupos desativados por ora — ver comentário nos estados acima.
                            <div className="select-wrapper">
                                <span className="popup-span">{tManagement("groups")}</span>
                                <CustomDropdown
                                    placeholder={tManagement("groupsPlaceholder")}
                                    options={availableGroups.map(g => g.name)}
                                    selected={grupos}
                                    setSelected={setGrupos}
                                    multiSelect
                                />
                            </div>
                            */}
                        </div>

                        <button className="popUp-button"
                            disabled={(!camposValidos && formTocado) || isRegistering}
                            onClick={handleCadastro}
                        >
                            <span>{isRegistering ? tManagement("salvando") : tManagement("salvar")}</span>
                        </button>

                        {showAlert && (
                            <div className="popup-alert">
                                <div className={`popup-alert-content ${alertType}`}>
                                    <div className="popup-alert-icon">
                                        {alertType === 'error' && '⚠️'}
                                        {alertType === 'success' && '✅'}
                                    </div>
                                    <div className="popup-alert-text">
                                        <span className="popup-alert-message">{alertMessage}</span>
                                    </div>
                                    <button
                                        className="popup-alert-close"
                                        onClick={() => setShowAlert(false)}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UsersContent;
